import React, { PureComponent } from 'react';
import { withRouter } from 'next/router';
import toInteger from '@nubank/nuds-web/utils/string/toInteger';

import JumpToContent from '@nubank/www-latam-commons/components/JumpToContent/JumpToContent';
import { sendEvent } from '@nubank/www-latam-commons/services/analytics';
import DrawerToggleButton from 'patterns/DrawerToggleButton/DrawerToggleButton';
import SEO from 'components/SEO/SEO';
import Header from 'components/ui/Header/Header';
import HeaderWrapper from 'components/ui/HeaderWrapper/HeaderWrapper';
import Footer from 'components/ui/Footer/Footer';
import MenuWrapper from 'components/ui/MenuWrapper/MenuWrapper';
import AnnouncementBar from 'components/AnnouncementBar/AnnouncementBar';
import { AnnouncementContextConsumer } from 'components/AnnouncementBar/AnnouncementBarProvider';
import { getProspectType, MULTI_PRODUCT } from 'utils/prospectTypes';
import { smoothScroll as handleLinkClick } from 'utils/domUtils';
import { trackOpenedWidget } from 'tracking/application/widget';
import { trackingClickMenu } from 'tracking/menu/clickInteraction';

import ProspectRegistrationDrawer from '../../patterns/ProspectRegistrationDrawer/ProspectRegistrationDrawer';

import findElementFrom from './utils/DOM/findElementFrom';
import { trackTimeOnPage } from './tracking';
import { Layout } from './styles/Layout';

export const headerTypes = {
  COMPLETE: 'COMPLETE',
  NONE: 'NONE',
  EMPTY: 'EMPTY',
  LANDING_PAGE: 'LANDING_PAGE',
  PRODUCT_PAGE: 'PRODUCT_PAGE',
  FEATURE_PAGE: 'FEATURE_PAGE',
};

export const footerTypes = {
  FULL: 'FULL',
  COMPANY_INFO: 'COMPANY_INFO',
  NONE: 'NONE',
  INSURANCE: 'INSURANCE',
};

const footerLayouts = {
  FULL: {
    navigation: true,
    company: true,
    insurance: false,
    languageSwitcher: true,
  },
  COMPANY_INFO: {
    navigation: false,
    company: true,
    insurance: false,
    languageSwitcher: false,
  },
  INSURANCE: {
    navigation: true,
    company: true,
    insurance: true,
    languageSwitcher: true,
  },
};

export const headerLayouts = {
  COMPLETE: {
    container: true,
    navigation: true,
    widget: true,
    anchor: false,
    login: true,
    waitScroll: false,
  },
  NONE: {
    container: false,
    navigation: false,
    login: false,
    widget: false,
    anchor: false,
    waitScroll: false,
  },
  EMPTY: {
    container: true,
    navigation: false,
    widget: false,
    anchor: false,
    login: false,
    waitScroll: false,
  },
  LANDING_PAGE: {
    container: true,
    navigation: false,
    widget: false,
    anchor: true,
    login: false,
    waitScroll: true,
  },
  PRODUCT_PAGE: {
    container: true,
    navigation: true,
    widget: false,
    anchor: true,
    login: true,
    waitScroll: false,
  },
  FEATURE_PAGE: {
    container: true,
    navigation: true,
    widget: false,
    anchor: true,
    login: false,
    waitScroll: false,
  },
};

function websitePage(options = {}) {
  const hocOptions = {
    widgetProspectType: MULTI_PRODUCT,
    routeKey: 'HOME',
    header: headerTypes.COMPLETE,
    footer: footerTypes.FULL,
    ...options,
  };
  const sendTimeOnPageMetric = trackTimeOnPage();

  return WrappedComponent => {
    class WebsitePage extends PureComponent {
      constructor(props) {
        super(props);

        this.state = {
          showAnchor: false,
          isAboveForm: true,
          isMobileWidgetVisible: true,
          isRegistrationFormOpen: false,
          registrationFormTrigger: null,
        };
      }

      componentWillUnmount = () => {
        sendTimeOnPageMetric();
      }

      handleToggleRegistration = ({ target } = {}) => {
        this.setState(({ isRegistrationFormOpen, registrationFormTrigger }) => ({
          isRegistrationFormOpen: !isRegistrationFormOpen,
          registrationFormTrigger: isRegistrationFormOpen
            ? registrationFormTrigger
            : findElementFrom(target, el => ['BUTTON', 'A'].includes(el.nodeName)),
        }), () => {
          const { registrationFormTrigger, isRegistrationFormOpen } = this.state;
          registrationFormTrigger.focus();

          if (isRegistrationFormOpen) {
            const { widgetProspectType } = hocOptions;
            const prospectType = getProspectType(widgetProspectType);
            const buttonIndex = registrationFormTrigger.getAttribute('data-open-widget-button-index');
            trackOpenedWidget({
              prospectType: prospectType.type,
              buttonIndex: toInteger(buttonIndex),
            });
            trackingClickMenu('open_widget');
          }
        });
      };

      handleFormVisibilityChange = showAnchor => {
        this.setState(() => ({
          showAnchor,
        }));
      };

      handleFormAnchorPosition = isAboveForm => {
        this.setState(() => ({
          isAboveForm,
        }));
      }

      handleWidgetVisibility = isMobileWidgetVisible => {
        this.setState({ isMobileWidgetVisible });
      }

      handleAnchorClick = event => {
        handleLinkClick(event);
        sendEvent('OPEN_FORM_ANCHOR_CLICKED');
      }

      renderWidget() {
        const { isRegistrationFormOpen, isMobileWidgetVisible } = this.state;
        const { widgetProspectType } = hocOptions;
        const prospectType = getProspectType(widgetProspectType);

        return (
          <>
            <ProspectRegistrationDrawer
              open={isRegistrationFormOpen}
              prospectType={prospectType}
              onClose={this.handleToggleRegistration}
              id="prospect-registration-drawer"
            />

            {isMobileWidgetVisible && (
              <DrawerToggleButton
                aria-expanded={isRegistrationFormOpen}
                buttonLabel={prospectType.buttonLabel}
                onToggleRegistration={this.handleToggleRegistration}
              />
            )}
          </>
        );
      }

      render() {
        const {
          showAnchor,
          isRegistrationFormOpen,
          isAboveForm,
        } = this.state;

        const {
          widgetProspectType,
          routeKey,
          footer,
          header,
        } = hocOptions;

        const prospectType = getProspectType(widgetProspectType);

        const {
          container: showHeader,
          navigation: allowNavigation,
          login: allowLogin,
          widget: allowWidget,
          anchor: allowAnchor,
          waitScroll,
        } = headerLayouts[header];

        const showFooter = footer !== footerTypes.NONE;
        const headerVisibility = Boolean(waitScroll && !showAnchor);
        const anchorIcon = isAboveForm ? 'arrow-up' : 'arrow-down';

        return (
          <>
            <SEO routeKey={routeKey} />

            <JumpToContent
              href="#main-content"
              intlKey="HEADER.JUMP_TO_CONTENT"
            />

            {allowWidget && this.renderWidget()}

            {showHeader && (
              <HeaderWrapper
                isHidden={headerVisibility}
                aria-hidden={headerVisibility}
              >
                <Header isHidden={headerVisibility}>
                  <MenuWrapper
                    onToggleRegistrationForm={this.handleToggleRegistration}
                    showNavigation={allowNavigation}
                    showLogin={allowLogin}
                    showAnchor={showAnchor}
                    isAnchorAllowed={allowAnchor}
                    isWidgetAllowed={allowWidget}
                    isRegistrationFormOpen={isRegistrationFormOpen}
                    anchorHandleClick={handleLinkClick}
                    prospectTypeIntlKey={prospectType.buttonLabel}
                    anchorIconName={anchorIcon}
                  />
                </Header>
                <AnnouncementContextConsumer>
                  {({
                    id,
                    isVisible,
                    handleClose: handleCloseAnnouncementBar,
                    handleClick: handleClickAnnouncementBar,
                  }) => (
                    <AnnouncementBar
                      identifier={id}
                      initiallyVisible={isVisible}
                      onClose={handleCloseAnnouncementBar}
                    >
                      <AnnouncementBar.Text intlKey="NAVIGATION_UX_SURVEY.TEXT" />
                      <AnnouncementBar.CTA
                        intlKey="NAVIGATION_UX_SURVEY.LINK"
                        href="https://nu.questionpro.com/t/APEUIZlJwI"
                        onClick={handleClickAnnouncementBar}
                      />
                    </AnnouncementBar>
                  )}
                </AnnouncementContextConsumer>
              </HeaderWrapper>
            )}
            <Layout
              hasMenuHeightPadding={showHeader && !waitScroll}
            >
              <div id="main-content">
                <WrappedComponent
                  {...this.props}
                  registrationButtonLabel={prospectType.buttonLabel}
                  onToggleRegistrationForm={this.handleToggleRegistration}
                  isRegistrationFormOpen={isRegistrationFormOpen}
                  onFormVisibilityChange={this.handleFormVisibilityChange}
                  onFormAnchorPosition={this.handleFormAnchorPosition}
                  onWidgetVisibilityChange={this.handleWidgetVisibility}
                  isAnchorAllowed={allowAnchor}
                />
              </div>
              {showFooter && <Footer {...footerLayouts[footer]} />}
            </Layout>
          </>
        );
      }
    }

    WebsitePage.getInitialProps = async ctx => {
      let pageProps = {};

      if (WrappedComponent.getInitialProps) {
        pageProps = await WrappedComponent.getInitialProps(ctx);
      }

      return {
        ...pageProps,
      };
    };

    return withRouter(WebsitePage);
  };
}

export default websitePage;
